import { Trans, useLingui } from '@lingui/react'
import { bool, element, type InferProps, string } from 'prop-types'
import type { FC, ReactElement } from 'react'

import IconDiscount from 'views/components/atoms/Icon/IconDiscount'
import IconFastTruckDelivery from 'views/components/atoms/Icon/IconFastTruckDelivery'
import IconHealth from 'views/components/atoms/Icon/IconHealth'
import Modal from 'views/components/molecules/Modal'
import Notification, {
  NOTIFICATION_TYPES,
} from 'views/components/molecules/Notification'

import './ErxSubstitutionModal.scss'

const BoxItemPropTypes = {
  title: string.isRequired,
  text: string.isRequired,
  rebrandingIcon: element,
}

type BoxItemProps = InferProps<typeof BoxItemPropTypes>

const BoxItem: FC<BoxItemProps> = ({
  title,
  text,
  rebrandingIcon,
}): ReactElement => {
  return (
    <div className="o-ErxSubstitutionModal__box-item-container">
      <div className="o-ErxSubstitutionModal__box-item-icon-container">
        {rebrandingIcon}
      </div>
      <div>
        <p className="o-ErxSubstitutionModal__box-item-title">{title}</p>
        <p className="o-ErxSubstitutionModal__box-item-description">{text}</p>
      </div>
    </div>
  )
}

BoxItem.propTypes = BoxItemPropTypes

const ErxSubstitutionModalPropTypes = {
  modalId: string.isRequired,
  originalName: string.isRequired,
  replacedName: string.isRequired,
  open: bool,
}

const ErxSubstitutionModalDefaultProps = {
  open: false,
}

type ErxSubstitutionModalProps = {
  modalId: string
  originalName: string
  replacedName: string
  open?: boolean
}

const ErxSubstitutionModal: FC<ErxSubstitutionModalProps> = ({
  modalId,
  originalName,
  replacedName,
  open,
}): ReactElement => {
  const { i18n } = useLingui()
  return (
    <Modal
      id={modalId}
      size="medium"
      light
      title={i18n._('account.orders.details.swappedErxProductModalTitle')}
      noPadding
      open={open}
    >
      <div className="u-background-color--greySky a-box a-box--medium">
        <h3 className="u-font-weight--bold o-ErxSubstitutionModal__replacement-title">
          <Trans id="account.orders.details.swappedErxProductModalReplacementTitle" />
        </h3>
        <Notification
          type={NOTIFICATION_TYPES.INFO}
          className="o-ErxSubstitutionModal__replacement-notification"
          label={
            <Trans
              id="account.orders.details.swappedErxProductModalReplacementText"
              components={{
                b: <b />,
                hightlight: (
                  <span className="o-ErxSubstitutionModal__notification-highlight-text" />
                ),
              }}
              values={{
                originalProduct: originalName,
                replacedProduct: replacedName,
              }}
            />
          }
        />
        <h3 className="u-font-weight--bold o-ErxSubstitutionModal__selection-title">
          <Trans id="account.orders.details.swappedErxProductModalSelectionTitle" />
        </h3>
        <div className="a-box a-box--medium a-box--white o-ErxSubstitutionModal__list-wrapper">
          <div className="u-padding-large--bottom u-margin-large--bottom u-border--bottom o-ErxSubstitutionModal__list-item-wrapper">
            <BoxItem
              title={i18n._(
                'account.orders.details.swappedErxProductModalCopaymentTitle'
              )}
              rebrandingIcon={<IconDiscount />}
              text={i18n._(
                'account.orders.details.swappedErxProductModalCopaymentText'
              )}
            />
          </div>
          <div className="u-padding-large--bottom u-margin-large--bottom u-border--bottom o-ErxSubstitutionModal__list-item-wrapper">
            <BoxItem
              title={i18n._(
                'account.orders.details.swappedErxProductModalDiscountTitle'
              )}
              rebrandingIcon={<IconHealth />}
              text={i18n._(
                'account.orders.details.swappedErxProductModalDiscountText'
              )}
            />
          </div>
          <BoxItem
            title={i18n._(
              'account.orders.details.swappedErxProductModalAvailabilityTitle'
            )}
            rebrandingIcon={<IconFastTruckDelivery />}
            text={i18n._(
              'account.orders.details.swappedErxProductModalAvailabilityText'
            )}
          />
        </div>
      </div>
    </Modal>
  )
}

ErxSubstitutionModal.propTypes = ErxSubstitutionModalPropTypes
ErxSubstitutionModal.defaultProps = ErxSubstitutionModalDefaultProps

export default ErxSubstitutionModal
